import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './TableList.css';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

const API_URL = 'https://www.fro.ro/api/clubs/public_clubs.php';
const STATUS_API = 'https://www.fro.ro/api/clubs/public_club_status.php';

export default function PublicClubs() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('status-active');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // ✅ doar an curent și următor (ex: 2025, 2026)
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current + 1];
  }, []);

  // === Fetch cluburi ===
  const fetchClubs = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_URL);
      const data = await response.json();

      if (data.success) {
        // verificăm statusul fiecărui club pentru anul selectat
        const clubsWithStatus = await Promise.all(
          data.clubs.map(async (club) => {
            try {
              const res = await fetch(`${STATUS_API}?club_id=${club.id}&year=${selectedYear}`);
              const stat = await res.json();
              const isActive = stat?.is_active === true;
              return { ...club, is_active: isActive };
            } catch {
              return { ...club, is_active: false };
            }
          })
        );
        setClubs(clubsWithStatus);
      } else {
        setClubs([]);
      }
    } catch (err) {
      console.error('Eroare la încărcare cluburi:', err);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, [selectedYear]);

  // === FILTRARE după search ===
  const filteredClubs = clubs
    // 🔹 eliminăm cluburile speciale
    .filter(c =>
      c.email !== 'admin@fro.ro' &&
      c.email !== 'cch@fro.ro'
    )
    // 🔹 aplicăm filtrarea clasică după nume
    .filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );

  // === SORTARE ===
  const sortedClubs = [...filteredClubs].sort((a, b) => {
    if (sortBy === 'status-active') return (b.is_active === true) - (a.is_active === true);
    if (sortBy === 'status-inactive') return (a.is_active === true) - (b.is_active === true);
    if (sortBy === 'name-az') return a.name.localeCompare(b.name);
    if (sortBy === 'name-za') return b.name.localeCompare(a.name);
    return 0;
  });

  return (
    <>
      <div className="athletes-container">
        <button onClick={() => navigate('/')} className="back-button">
          <ArrowLeft size={18} strokeWidth={2.2} />
        </button>
        <h1 className="title">Cluburi Sportive</h1>
      </div>

      <div className="table-list-container">
        <div className="table-list-header">

          {/* 🔹 Selector an */}
          <div className="year-selector">
            <label>An:</label>
            <select
              className="year-dropdown"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y === years[0] ? `${y} (curent)` : `${y} (următor)`}
                </option>
              ))}
            </select>
          </div>

          {/* 🔹 Sortare */}
          <div className="sort-bar">
            <label htmlFor="sort-select" className="sort-label">Sortează:</label>
            <select
              id="sort-select"
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="status-active">Status (activi primii)</option>
              <option value="status-inactive">Status (inactivi primii)</option>
              <option value="name-az">A–Z</option>
              <option value="name-za">Z–A</option>
            </select>
          </div>

          {/* 🔹 Căutare */}
          <div className="search-container">
            <input
              type="text"
              className="search-bar"
              placeholder="Caută club..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* === TABEL === */}
        <table className="table-list">
          <thead>
            <tr>
              <th>Nume Club</th>
              <th>Email</th>
              <th>Persoană Contact</th>
              <th>Status ({selectedYear})</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 40 }}>
                  Se încarcă cluburile...
                </td>
              </tr>
            ) : sortedClubs.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', padding: 40 }}>
                  Nu există cluburi pentru anul {selectedYear}.
                </td>
              </tr>
            ) : (
              sortedClubs.map((club, idx) => (
                <tr key={idx} className={club.is_active ? '' : 'inactive-row'}>
                  <td>{club.name}</td>
                  <td>{club.email}</td>
                  <td>{club.contact_name || '-'}</td>
                  <td>
                    <span
                      className={`status-badge ${club.is_active ? 'active' : 'inactive'}`}
                    >
                      <span className="status-dot"></span>
                      {club.is_active ? 'Activ' : 'Inactiv'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
